import { useState } from 'react';
import type { MiceStatusCounts } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Dialog, DialogContent, DialogTitle, Dot } from '@web/ui/core';
import type { BudgetLineState } from '../hooks/use_mice_budget';
import { BudgetControl } from './budget_section';
import { executionStats } from './execution_summary';

interface TravelersStatusLineProps {
  counts: MiceStatusCounts | undefined;
  /** Viajantes na fase Atenção: o número do link que abre essa fase. */
  needsYou: number;
  /** Quantas necessidades aprovadas ainda não viraram pedido. */
  approved: number;
  /** Verdes do evento para "Emitir N verdes"; 0 esconde o botão. */
  green: number;
  canPurchase: boolean;
  /** Falso só com premissas por conferir e o evento já com viajantes. */
  premisesChecked: boolean;
  onNeedsYou: () => void;
  onReviewApproved: () => void;
  /** Pede todas as verdes; `onDone` roda só quando o servidor aceitou. */
  onEmitGreen: (onDone: () => void) => void;
  /** Uma ação em lote em andamento: "Emitir N verdes" espera ela voltar. */
  busy: boolean;
  onCheckPremises: () => void;
  /** Quem gera o orçamento (`mice:manage` e `mice:purchase`): o botão toma o lugar de "Conferir",
   *  porque gerar o orçamento é conferir as premissas. Null mantém o aviso com "Conferir". */
  budget: BudgetLineState | null;
}

/** Texto que age: um botão com cara de link, para caber no meio de uma frase. */
function LinkButton({
  onClick,
  children,
}: Readonly<{ onClick: () => void; children: string }>): React.JSX.Element {
  return (
    <button
      type="button"
      className="font-medium text-foreground underline underline-offset-2 hover:text-primary"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

const Sep = (): React.JSX.Element => <span aria-hidden>·</span>;

type Stats = ReturnType<typeof executionStats>;

/** Em andamento: algo anda ou espera uma pessoa. Concluída: todos os pedidos emitidos. Só pedidos
 *  prontos (planilha ou fora da fila) não dizem nada. */
function purchasePhase(s: Stats | null): 'running' | 'done' | null {
  if (s === null) return null;
  if (s.queued + s.buying + s.awaitingIssuance + s.problem > 0) return 'running';
  return s.total > 0 && s.issued === s.total ? 'done' : null;
}

function PurchasePhrase({
  stats,
  phase,
  needsYou,
  onNeedsYou,
}: Readonly<{
  stats: Stats;
  phase: 'running' | 'done';
  needsYou: number;
  onNeedsYou: () => void;
}>): React.JSX.Element {
  const running = phase === 'running';
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Dot tone="green" pulse={running} />
      <span className="font-medium text-foreground">
        {t(running ? 'mice.exec.live' : 'mice.status.done')}
      </span>
      <Sep />
      <span>{t('mice.exec.title', { issued: stats.issued, total: stats.total })}</span>
      {stats.awaitingIssuance > 0 ? (
        <>
          <Sep />
          <span>{t('mice.status.awaiting', { count: stats.awaitingIssuance })}</span>
        </>
      ) : null}
      {needsYou > 0 ? (
        <>
          <Sep />
          <LinkButton onClick={onNeedsYou}>
            {t('mice.exec.attentionLink', { count: needsYou })}
          </LinkButton>
        </>
      ) : null}
    </span>
  );
}

/** "Emitir N verdes" com a confirmação: verdes não têm voucher a decidir nem cadastro pendente. */
function EmitGreenButton({
  count,
  busy,
  onConfirm,
}: Readonly<{
  count: number;
  busy: boolean;
  onConfirm: (onDone: () => void) => void;
}>): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const label = t('mice.status.emitGreen', { count });
  return (
    <>
      <Button
        size="sm"
        variant="primary"
        disabled={busy}
        onClick={() => {
          setOpen(true);
        }}
      >
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogTitle>{t('mice.status.emitGreenConfirm', { count })}</DialogTitle>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('mice.status.emitGreenCancel')}
            </Button>
            {/* Fecha só quando o servidor aceita; enquanto isso, um segundo clique não posta. */}
            <Button
              disabled={busy}
              onClick={() => {
                onConfirm(() => {
                  setOpen(false);
                });
              }}
            >
              {label}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** O orçamento (para quem o gera) ou o aviso das premissas por conferir com "Conferir". */
function PremisesSlot({
  budget,
  premisesChecked,
  onCheckPremises,
}: Readonly<
  Pick<TravelersStatusLineProps, 'budget' | 'premisesChecked' | 'onCheckPremises'>
>): React.JSX.Element | null {
  if (budget) return <BudgetControl {...budget} />;
  if (premisesChecked) return null;
  return (
    <span className="flex items-center gap-1.5 text-tone-amber-ink">
      {t('mice.premises.unchecked')}
      <Sep />
      <LinkButton onClick={onCheckPremises}>{t('mice.status.premisesCheck')}</LinkButton>
    </span>
  );
}

/**
 * A frase da compra acima da grid ("● Compra em andamento · 0 de 8 emitidos · 6 precisam de
 * você"), "Emitir N verdes" e "Revisar e comprar N" à direita e o botão do orçamento ou o aviso
 * discreto das premissas por conferir. Sem nada a dizer, não aparece.
 */
export function TravelersStatusLine(props: Readonly<TravelersStatusLineProps>) {
  const stats = props.counts ? executionStats(props.counts) : null;
  const phase = purchasePhase(stats);
  const review = props.approved > 0 && props.canPurchase;
  const emit = props.green > 0 && props.canPurchase;
  const slot = props.budget !== null || !props.premisesChecked;
  if (phase === null && !review && !emit && !slot) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-meta text-foreground-default">
      {stats && phase ? (
        <PurchasePhrase
          stats={stats}
          phase={phase}
          needsYou={props.needsYou}
          onNeedsYou={props.onNeedsYou}
        />
      ) : null}
      <PremisesSlot
        budget={props.budget}
        premisesChecked={props.premisesChecked}
        onCheckPremises={props.onCheckPremises}
      />
      <span className="flex-1" />
      {emit ? (
        <EmitGreenButton count={props.green} busy={props.busy} onConfirm={props.onEmitGreen} />
      ) : null}
      {review ? (
        <Button
          size="sm"
          variant="primary"
          aria-label={t('mice.status.reviewLabel', { count: props.approved })}
          onClick={props.onReviewApproved}
        >
          {t('mice.status.review', { count: props.approved })}
        </Button>
      ) : null}
    </div>
  );
}
